import React, {useState, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useXpertKnowledgeContext} from '../XpertKnowledge/XpertKnowledgeContext'
import {Col, Block, Row} from 'jsxstyle'
import {ReactComponent as DeleteIcon} from '../../common/assets/delete-icon.svg'
import Loader from '../../components/Loader'
import {ReactComponent as LeftArrowIcon} from '../../common/assets/left-arrow-icon.svg'
import i18n from '../../common/i18n'

const XpertKnowledgeAssets: React.FC = () => {
  const {xpertKnowledgeId} = useParams<{xpertKnowledgeId: string}>()
  const {fetchXpertKnowledgeAssets} = useXpertKnowledgeContext()
  const navigate = useNavigate()

  const [recordingUrl, setRecordingUrl] = useState<string | undefined>(undefined)
  const [eventsJson, setEventsJson] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const loadAssets = async () => {
      try {
        const assetData = await fetchXpertKnowledgeAssets(xpertKnowledgeId!)
        const recUrl = assetData.recordingBlob?.url
        const events = assetData.mergedJsonBlob?.phrases

        if (!recUrl || !events) {
          setError('Failed to load assets.')
        } else {
          setRecordingUrl(recUrl)
          setEventsJson(JSON.stringify(events, null, 2))
        }
      } catch (err) {
        setError('Failed to load assets.')
        console.error('Error fetching assets:', err)
      } finally {
        setIsLoaded(true)
      }
    }

    if (xpertKnowledgeId) {
      loadAssets()
    }
  }, [xpertKnowledgeId, fetchXpertKnowledgeAssets])

  if (!isLoaded) {
    return (
      <Block width="100vw" height="100vh" display="flex" justifyContent="center" alignItems="center">
        <Col gap="10px" justifyContent="center" alignItems="center" textAlign="center">
          <Loader size={75} />
          <Block fontWeight={500} lineHeight="60px" fontSize="34px">
            {i18n.t('xk.tutorial.assets.loading')}
          </Block>
        </Col>
      </Block>
    )
  }

  if (error) {
    return (
      <Col height="100vh" justifyContent="center" alignItems="center" gap="16px" backgroundColor="#f8d7da" padding="20px">
        <Block>
          <DeleteIcon width="80px" height="80px" fill="#721c24" />
        </Block>
        <Block fontSize="32px" fontWeight="bold" color="#721c24">
          {i18n.t('xk.tutorial.assets.error')}
        </Block>
        <Block fontSize="18px" color="#721c24">
          {error}
        </Block>
        <Block height="1px" width="50%" backgroundColor="#721c24" margin="12px 0" />
        <Block fontSize="16px" color="#721c24">
          {i18n.t('xk.tutorial.assets.tryAgain')}
        </Block>
      </Col>
    )
  }

  return (
    <Col padding="20px" backgroundColor="#FFFFFF" minHeight="100vh">
      <Row gap="10px" alignItems="center" marginBottom="20px" justifyContent="space-between">
        <Block fontSize="28px" fontWeight="bold" color="#100E11" marginLeft="12px">
          {i18n.t('xk.tutorial.assets.title')}
        </Block>
        <Row
          alignItems="center"
          gap="8px"
          backgroundColor="#0E78F5"
          hoverBackgroundColor="#0053BA"
          borderRadius="6px"
          padding="10px 16px"
          cursor="pointer"
          width="fit-content"
          props={{
            onClick: () => navigate(`/xpert-knowledge/${xpertKnowledgeId}/tutorial`),
          }}
        >
          <LeftArrowIcon height={20} width={20} color="#FFFFFF" />
          <Block color="#FFFFFF" fontSize="16px" fontWeight={500}>
            {i18n.t('xk.tutorial.assets.back')}
          </Block>
        </Row>
      </Row>

      <Row gap="20px">
        <Col width="50%" padding="10px" className="ph-no-capture">
          <Block fontSize="28px" fontWeight="bold" color="#100E11" marginBottom="16px">
            {i18n.t('xk.tutorial.assets.recording')}
          </Block>
          {recordingUrl && (
            <video controls width="100%" controlsList="nodownload">
              <source src={recordingUrl} type="video/mp4" />
              Your browser does not support the video element.
            </video>
          )}
        </Col>
        <Col width="50%" padding="10px">
          <Block fontSize="28px" fontWeight="bold" color="#100E11" marginBottom="16px">
            {i18n.t('xk.tutorial.assets.transcript')}
          </Block>
          <Block
            backgroundColor="#f8f9fa"
            padding="16px"
            border="1px solid #DADCE0"
            borderRadius="4px"
            fontFamily="monospace"
            fontSize="14px"
            whiteSpace="pre-wrap"
            maxHeight="64vh"
            overflowY="auto"
          >
            {eventsJson}
          </Block>
        </Col>
      </Row>
    </Col>
  )
}

export default XpertKnowledgeAssets
